import React from 'react';
import { Link } from 'react-router-dom';
import { useForm, UseFormReturn, FieldErrors } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import styled from '@emotion/styled';
import { Stack, Form } from 'react-bootstrap';

import SVButton from 'components/elements/SVButton';
import { FlexJustifyRight } from 'resources/styles/display';

const FormLabel = styled(Form.Label)`
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 0;
`;

const formTextStyle = `
  border: 0;
  border-bottom: 1px solid #ced4da;
  border-radius: 0;
`;

const FormText = styled(Form.Control)`
  ${({ as }: { as?: string }) => (as ? '' : formTextStyle)}
`;

export interface ReportFormValues {
  name: string;
  embedPath: string;
  jwtEmbedPath?: string;
  dataInclusionNote?: string;
  description?: string;
}

interface FormInputProps {
  name: keyof ReportFormValues;
  label?: string;
  required?: boolean;
  form: UseFormReturn<ReportFormValues, any, ReportFormValues>;
  [key: string]: any;
}

const requiredTag = (required?: boolean) => (required ? (
  <span style={{ marginLeft: '5px', fontSize: '1.5rem', color: 'red' }}>
    *
  </span>
) : null);

const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  required,
  form: {
    register,
    formState: { errors },
  },
  ...inputProps
}) => (
  <Form.Group className="mb-3" controlId={name}>
    {label && (
      <FormLabel>
        {label}
        {requiredTag(required)}
      </FormLabel>
    )}
    <FormText
      required={required}
      {...register(name)}
      {...inputProps}
      isInvalid={!!errors[name]}
    />
    <Form.Control.Feedback type="invalid">
      {(errors as FieldErrors)[name]?.message as string}
    </Form.Control.Feedback>
  </Form.Group>
);

const schema: yup.ObjectSchema<ReportFormValues> = yup.object({
  name: yup.string().required(),
  embedPath: yup.string().url('Invalid URL').required(),
  jwtEmbedPath: yup.string().url('Invalid URL').optional(),
  dataInclusionNote: yup.string().optional(),
  description: yup.string().optional(),
});

export interface ReportFormProps {
  defaultValues?: Partial<ReportFormValues>;
  onSubmit: (data: ReportFormValues) => void;
}

const ReportForm: React.FC<ReportFormProps> = ({
  defaultValues = {},
  onSubmit,
}) => {
  const formProps = useForm<ReportFormValues, any, ReportFormValues>({
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { handleSubmit } = formProps;

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FlexJustifyRight>
        <Stack direction="horizontal" gap={2}>
          <SVButton variant="cancel" as={Link as any} {...{ to: '..' }}>
            Cancel
          </SVButton>
          <SVButton variant="secondary" type="submit">
            Save
          </SVButton>
        </Stack>
      </FlexJustifyRight>
      <FormInput label="Report Name" name="name" form={formProps} required />
      <FormInput
        label="Embed Path"
        name="embedPath"
        form={formProps}
        required
      />
      <FormInput
        label="JWT Embed Path"
        name="jwtEmbedPath"
        form={formProps}
      />
      <FormInput
        label="Data Inclusion Note"
        name="dataInclusionNote"
        form={formProps}
      />
      <FormInput
        label="Description"
        name="description"
        form={formProps}
        rows={4}
        as="textarea"
      />
    </Form>
  );
};

export default ReportForm;
